import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import styles from "./PortfolioGalleryPage.module.scss";

import Header from "../Header/header";
import SetupSteps, { Step } from "../Register/SetupSteps/SetupSteps";

const STEPS: Step[] = [
  { key: "practice", label: "Practice and personal info", path: "/practiceDetails" },
  { key: "logo", label: "Logo Management", path: "/logoManagement" },
  { key: "social", label: "Social Media Links", path: "/socialLinks" },
  { key: "portfolio", label: "Portfolio Gallery", path: "/portfolioGallery" },
  { key: "consent", label: "Consent Templates", path: "/consentTemplates" },
  { key: "preview", label: "Preview & Complete", path: "/previewComplete" },
];

type ImgMeta = { name: string; size: number; preview: string };

export default function PortfolioGalleryPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [gallery, setGallery] = useState<ImgMeta[]>([]);
  const [successModal, setSuccessModal] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);

  const token = axios.defaults.headers.common.Authorization;

  /* --------------------------- Fetch user data --------------------------- */
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await axios.get("/user");
        if (Array.isArray(data) && data.length > 1) {
          setUser(data[1]);
        }
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };
    fetchUser();
  }, []);

  /* ----------------------------- File uploader ---------------------------- */
  const openPicker = () => fileInputRef.current?.click();

  const showSuccessModal = (message: string) => {
    setSuccessModal(message);
    setTimeout(() => setSuccessModal(null), 2000);
  };

  const uploadFile = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("is_confidential", "false");
      formData.append("user_file_name", file.name);
      formData.append("keywords", "");
      formData.append("category_ids", "9");

      await axios.post(
      `/consent_clause/upload_user_media_to_s3`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: token,
          },
        }
      );

      showSuccessModal(`${file.name} uploaded successfully ✅`);
    } catch (err) {
      console.error("Upload failed:", err);
      showSuccessModal(`❌ Failed to upload ${file.name}`);
    }
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const accepted: ImgMeta[] = [];
    Array.from(files).forEach((f) => {
      const okType = /image\/(png|jpeg)/i.test(f.type);
      const okSize = f.size <= 5 * 1024 * 1024;

      if (okType && okSize) {
        const preview = URL.createObjectURL(f);
        accepted.push({ name: f.name, size: f.size, preview });
        uploadFile(f);
      } else {
        showSuccessModal(`${f.name} is invalid ❌`);
      }
    });

    if (accepted.length === 0) return;
    setGallery((prev) => [...prev, ...accepted]);

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  /* ----------------------------- UI Render ----------------------------- */
  return (
    <div className={styles.page}>
      <Header />
      <main className={styles.main}>
         <SetupSteps
          steps={STEPS}
          current={4}
          rightLabel="50% Complete"
          onStepClick={(step) => navigate(step.path)}
        />


        <div className={styles.headerRow}>
          <div>
            <h1 className={styles.title}>Portfolio gallery</h1>
            <p className={styles.subtitle}>
              Upload before-and-after photos, treatment videos, or educational
              clips. These visuals can be included in your consent forms and used as
              a marketing pack to highlight your work.
            </p>
          </div>
        </div>

        {/* Upload section */}
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Upload images</h2>
          <div
            className={styles.drop}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            role="button"
            tabIndex={0}
            aria-label="Upload portfolio images"
          >
            <div className={styles.dropInner}>
              <div className={styles.dropTitle}>Drag & drop or click below</div>
              <div className={styles.dropHint}>PNG or JPG, max 5MB each</div>
              <button
                type="button"
                className={styles.chooseBtn}
                onClick={openPicker}
              >
                Add images
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                multiple
                className={styles.fileInput}
                onChange={(e) => handleFiles(e.target.files)}
              />
            </div>
          </div>
        </section>

        {/* Tips */}
        <section className={`${styles.card} ${styles.tipsCard}`}>
          <h3 className={styles.tipsTitle}>Portfolio tips for the best results:</h3>
          <ul className={styles.tipsList}>
            <li>
              Use it as both a practice portfolio and a source of content for your
              website or promotional materials.
            </li>
            <li>
              Showcase your individual expertise as a dentist, not just your
              practice’s brand.
            </li>
            <li>
              Include before-and-after examples of different procedures to build
              patient trust.
            </li>
            <li>
              Share a variety of treatments to demonstrate your full skill set.
            </li>
            <li>
              Always use high-quality images and videos to put your best work forward.
            </li>
          </ul>
        </section>

        {/* Gallery preview */}
        <div className={styles.countGrid}>
          <div className={styles.countCard}>
            <div className={styles.countTitle}>
              Portfolio ({gallery.length} images)
            </div>
            <div className={styles.countBody}>
              {gallery.length === 0 ? (
                "No images uploaded yet"
              ) : (
                <ul className={styles.previewList}>
                  {gallery.map((m, i) => (
                    <li key={i} className={styles.previewItem}>
                      <img
                        src={m.preview}
                        alt={m.name}
                        className={styles.previewThumb}
                      />
                      <span>{m.name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <button
            className={styles.btnSecondary}
            onClick={() => navigate("/socialLinks")}
          >
            Back
          </button>
          <button
            className={styles.btnPrimary}
            onClick={() => navigate("/consentTemplates")}
          >
            Next
          </button>
        </div>
      </main>

      {/* Success Modal */}
      {successModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalBox}>{successModal}</div>
        </div>
      )}

      <footer className={styles.footerBar}>
        Copyright © Medicube 2025. All Rights Reserved.
      </footer>
    </div>
  );
}
